import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useInfiniteQuery } from '@/hooks/api/useInfiniteQuery';
import { AxiosResponse } from 'axios';
import { AssetDependency } from '@/api/generatedApi';
import { useMemo } from 'react';

export const GET_ASSET_DEPENDENCIES_QUERY_KEY = 'assets.getAssetDependencies';

interface GetAssetDependenciesResponse {
  dependencies: AssetDependency[];
  pageData: {
    total: number;
    currentPage: number;
    perPage: number;
    lastPage: number;
  };
}

export const useGetAssetDependencies = ({ assetId, enabled = true }: { assetId?: string; enabled?: boolean }) => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const result = useInfiniteQuery<{ page?: number; perPage?: number }, GetAssetDependenciesResponse, 'dependencies'>({
    queryKey: [GET_ASSET_DEPENDENCIES_QUERY_KEY, { assetId, tenantId }],
    queryFunction: ({
      page,
      perPage,
    }: {
      page?: number;
      perPage?: number;
    }): Promise<AxiosResponse<GetAssetDependenciesResponse>> =>
      api.assets.getAssetDependenciesById({
        tenantId: tenantId,
        assetId: assetId ?? '',
        page: page ?? 1,
        perPage: perPage!,
      }),
    queryParams: {},
    dataKey: 'dependencies',
    options: {
      enabled: enabled && !!assetId && !!tenantId,
    },
  });

  // Deduplicate dependencies by path to prevent duplicate IDs when merging paginated results
  const uniqueDependencies = useMemo(() => {
    const seen = new Set<string>();
    return (result.dependencies as AssetDependency[]).filter(dep => !seen.has(dep.path) && seen.add(dep.path));
  }, [result.dependencies]);

  return {
    dependencies: uniqueDependencies,
    pageData: result.rawResult.data?.pages[0]?.data.pageData,
    isLoading: result.isLoading,
    error: result.error,
    refetch: () => result.rawResult.refetch(),
    fetchNextPage: result.fetchNextPage,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
  };
};
